<template>
  <div style="height: 100%; position: relative;">
    <DashboardFilterChip :chip-color="chipColor" :chip-text="chipText"></DashboardFilterChip>
    <v-card style="height: 100%;">
      <v-card-text>
        <v-row class="mt-3">
          <v-col cols="12" align="center">
            <h1>
              Interviews
            </h1>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" class="px-5">
            <v-row justify="end" class="mb-2">
              <v-col cols="auto">
                <ButtonCustomIcon
                  icon="cloud_download"
                  color="primary"
                  text="Excel"
                  :disabled="!interviews || interviews.length === 0"
                  @click="handleExportToExcel"
                />
              </v-col>
            </v-row>
            <v-data-table
              :headers="interviewsHeaders"
              :items="rows"
              :calculate-widths="true"
              :items-per-page="5"
              style="height: 350px; overflow: auto;"
            >
              <template v-slot:item.date="{ item }">
                <span
                  :class="{
                    'for-today': isForToday(item.date),
                    'for-tomorrow': isForTomorrow(item.date)
                  }"
                >
                  {{ formatInterviewDateTime(item) }}
                </span>
              </template>
              <template v-slot:item.client_Name="{ item }">
                {{ item.client_Name ? item.client_Name.name : '' }}
              </template>
              <template v-slot:item.job_Opening_Name="{ item }">
                {{ item.job_Opening_Name ? item.job_Opening_Name.name : '' }}
              </template>
              <template v-slot:item.interview_Name="{ item }">
                {{ item.interview_Name }}
              </template>
              <template v-slot:item.candidate_Name="{ item }">
                {{ item.candidate_Name ? item.candidate_Name.name : '' }}
              </template>
              <template v-slot:item.interviewers="{ item }">
                {{ item.interviewers }}
              </template>
              <template v-slot:item.interview_Link="{ item }">
                <a
                  v-if="item.interview_Link"
                  :href="item.interview_Link"
                  target="_blank"
                  style="text-decoration: none; text-transform: uppercase; font-size: .875rem;"
                >
                  <v-btn
                    class="mx-1 my-1 hide-on-print"
                    color="primary"
                    :disabled="disableButtons"
                    outlined
                  >
                    <v-icon color="primary">info</v-icon>
                  </v-btn>
                </a>
              </template>
            </v-data-table>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" align="end">
            <v-btn
              v-if="!showFetchingDataGIF"
              color="primary"
              outlined
              class="ml-2"
              @click="refreshTile"
              style="float: left;"
              >
                <v-icon>refresh</v-icon>
            </v-btn>
            <i
              v-if="showFetchingDataGIF"
              class="mr-2"
            >
              Fetching Data
            </i>
            <v-progress-circular
              v-if="showFetchingDataGIF"
              :size="30"
              :width="7"
              indeterminate
              color="primary"
            ></v-progress-circular>
            <i
              v-if="lastRefreshTime"
              class="ml-10 mr-5"
            >
              Last Update: {{ lastRefreshTime }}
            </i>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import { getLocalTimeString } from '@/lib/display'
import { mapActions } from 'vuex'
import { isJSONString } from '@/lib/miscellaneous'
import { ButtonCustomIcon } from '@/components/buttons'
import DashboardFilterChip from '@/components/dashboard/DashboardFilterChip'
import excel from '@/lib/export/excel'
import { formatInterviewDateTime } from './interviewDateTimeHelpers'

export default {
  components: {
    ButtonCustomIcon,
    DashboardFilterChip
  },
  props: {
    chipText: {
      type: String,
      default: 'All Business Entity'
    },
    chipColor: {
      type: String,
      default: 'primary'
    }
  },
  data () {
    return {
      interviews: [],
      interviewsHeaders: [
        { text: 'Date', value: 'date', width: '30%', align: 'center' },
        { text: 'Client', value: 'client_Name', width: '14.6%' },
        { text: 'Job Opening', value: 'job_Opening_Name', width: '14.6%' },
        { text: 'Interviewers', value: 'interviewers', width: '14.6%' },
        { text: 'Candidate', value: 'candidate_Name', width: '14.6%' },
        { text: 'Details', value: 'interview_Link', width: '10%', align: 'center', sortable: false }
      ],
      lastRefreshTime: null,
      showFetchingDataGIF: false,
      disableButtons: false
    }
  },
  computed: {
    rows () {
      let rows = []
      if (this.interviews && this.interviews.length > 0) {
        rows = rows.concat(this.interviews)
      }
      if (rows.length < 5) {
        let missingRowsCount = 5 - rows.length
        for (let i = 0; i < missingRowsCount; i++) {
          rows.push({
            date: null,
            client_Name: null,
            job_Opening_Name: null,
            interviewers: null,
            candidate_Name: null,
            interview_Link: null
          })
        }
      }
      return rows
    }
  },
  methods: {
    ...mapActions('dashboard', [
      'getUpcomingInterviews'
    ]),
    formatInterviewDateTime,
    handleExportToExcel () {
      if (!this.interviews || this.interviews.length === 0) {
        return
      }

      const data = this.interviews.map(item => ({
        date: formatInterviewDateTime(item),
        client: item.client_Name ? item.client_Name.name : '',
        jobOpening: item.job_Opening_Name ? item.job_Opening_Name.name : '',
        interviewers: item.interviewers,
        candidate: item.candidate_Name ? item.candidate_Name.name : '',
        details: item.interview_Link
      }))

      excel.exportArray2Excel({
        title: ['Date', 'Client', 'Job Opening', 'Interviewers', 'Candidate', 'Details'],
        key: ['date', 'client', 'jobOpening', 'interviewers', 'candidate', 'details'],
        data,
        autoWidthFlag: true,
        filename: 'Upcoming Interviews'
      })
    },
    isForToday (date) {
      return new Date(date).setHours(0, 0, 0, 0) ===
        new Date().setHours(0, 0, 0, 0)
    },
    isForTomorrow (targetDate) {
      let completedAt = new Date()
      const tomorrowDueDate = new Date(targetDate).setHours(0, 0, 0, 0)
      const tomorrowDate = new Date(new Date(completedAt).getFullYear(), new Date(completedAt).getMonth(), new Date(completedAt).getDate(completedAt) + 1).setHours(0, 0, 0, 0)
      return tomorrowDate === tomorrowDueDate
    },
    refreshTile () {
      this.showFetchingDataGIF = true
      this.disableButtons = true
      this.getUpcomingInterviews().then((data) => {
        let dateTimeNow = new Date()
        this.interviews = data
        localStorage.setItem('dashboard:upcoming-interviews', JSON.stringify(data))
        localStorage.setItem('dashboard:upcoming-interviews-last-refresh-time', JSON.stringify(dateTimeNow.toUTCString()))
        this.lastRefreshTime = getLocalTimeString(dateTimeNow)
        this.showFetchingDataGIF = false
        this.disableButtons = false
      })
    }
  },
  created () {
    if (localStorage.getItem('dashboard:upcoming-interviews') && isJSONString(localStorage.getItem('dashboard:upcoming-interviews'))) {
      this.interviews = JSON.parse(localStorage.getItem('dashboard:upcoming-interviews'))
    }
    if (localStorage.getItem('dashboard:upcoming-interviews-last-refresh-time') && isJSONString(localStorage.getItem('dashboard:upcoming-interviews-last-refresh-time'))) {
      let lastRefreshTime = new Date(JSON.parse(localStorage.getItem('dashboard:upcoming-interviews-last-refresh-time')))
      this.lastRefreshTime = getLocalTimeString(lastRefreshTime)
    }
    this.refreshTile()
  }
}
</script>

<style>
  .for-today {
    color: black;
    letter-spacing: 1px;
    border: 1px solid orange;
    background-color: orange;
    border-radius: 10px;
    padding: 1px 15px 1px 15px;
    display: block;
    text-align: center;
  }

  .for-tomorrow {
    color: black;
    border: 1px solid yellow;
    background-color: yellow;
    border-radius: 10px;
    padding: 1px 15px 1px 15px;
    display: block;
    text-align: center;
  }
</style>
